import { Injectable } from '@angular/core';

import { Coupon } from '@cds/betting-offer/domain-specific/coupons';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MultiBuilderConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { IntlService } from '@frontend/vanilla/core';
import { flatten, groupBy, keys, uniq } from 'lodash-es';
import { Observable } from 'rxjs';

import { CouponApi } from '../cds/coupon-api.service';
import { EventsByDay, MarketGroupItem, MultiBuilderConstants } from './multi-builder.model';

@Injectable()
export class MultiBuilderService {
    private cache: ExpiringCache;
    private _isComboErrorToasterOpened = false;
    constructor(
        private sitecore: Sitecore,
        private competitionLogosService: CompetitionLogosService,
        private intlService: IntlService,
        private multiBuilderConfig: MultiBuilderConfig,
        private couponApi: CouponApi,
    ) {}

    groupEvents(events: GridEvent[]): EventsByDay[] {
        const today = new Date();
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        let upComingEvents: GridEvent[] = [];
        upComingEvents = events.filter((event) => !event.live);
        const multiBuilderEvents: EventsByDay[] = [{ day: this.sitecore.globalMessages.NavLive, fixtures: events.filter((event) => event.live) }];
        const groupedFixtures = groupBy(
            upComingEvents,
            (event) => new Date(event.startDate.getFullYear(), event.startDate.getMonth(), event.startDate.getDate()),
        );

        const upcomingEvents = keys(groupedFixtures).map((key) => ({
            day:
                this.intlService.formatDate(new Date(key), 'shortDate') === this.intlService.formatDate(today, 'shortDate')
                    ? this.sitecore.globalMessages.Today
                    : this.intlService.formatDate(new Date(key), 'shortDate') === this.intlService.formatDate(tomorrow, 'shortDate')
                      ? this.sitecore.globalMessages.Tomorrow
                      : this.intlService.formatDate(new Date(key), 'shortDate'),
            fixtures: groupedFixtures[key],
        }));
        upcomingEvents.forEach((f) => {
            multiBuilderEvents.push({ day: f.day, fixtures: f.fixtures });
        });

        return multiBuilderEvents;
    }

    isParticipantImageAvailable(event: GridEvent): boolean {
        const isLogoAvailable =
            event.participants.every((participant) => participant.image && participant.image.logo) &&
            this.competitionLogosService.hasLogo(event.sport.id, event.league.id);

        const isJerseyAvailable = event.participants.every((participant) => participant.image && participant.image.jersey && !participant.image.logo);

        return isJerseyAvailable || isLogoAvailable;
    }

    getMarketGridGroups(events: GridEvent[]): MarketGroupItem[] {
        let optionGroups: EventOptionGroup[] = [];
        let groups: MarketGroupItem[] = [];
        optionGroups = uniq(flatten(events.map((f) => f.optionGroups)));
        groups = optionGroups.map((o) => {
            return { id: (o.gridGroups && o.gridGroups[0]) || '', title: o.name, selected: false };
        });

        return this.filterMarketGroups(groups, events[0].sport.id);
    }

    private filterMarketGroups(groups: MarketGroupItem[], sportId: number): MarketGroupItem[] {
        const sportGridGroups = this.multiBuilderConfig.marketGridGroups.filter((item) => item.sportId === sportId);
        const filteredGroups: MarketGroupItem[] = [];
        if (sportGridGroups.length) {
            sportGridGroups[0].gridGroups.forEach((item) => {
                const marketGroup = groups.find((group) => group.id === item.id);

                if (marketGroup) {
                    marketGroup.title = this.sitecore.multiBuilder.messages[marketGroup.id] || marketGroup.title;
                    marketGroup.showOptionName = item.showOptionName;
                    marketGroup.showColumnName = item.showColumnName;
                    marketGroup.sportId = sportId;
                    filteredGroups.push(marketGroup);
                }
            });
        }

        return filteredGroups;
    }

    getMultibuilderCoupons(): Observable<Coupon[] | undefined> {
        if (!this.cache) {
            this.cache = new ExpiringCache(this.multiBuilderConfig.cacheTimeOut);
        }

        return this.cache.getOrCreate(
            MultiBuilderConstants.MultibuilderCacheKey,
            this.couponApi.getMultiBuilderCoupons(this.multiBuilderConfig.maxCompetitionsCount),
        );
    }

    get isComboErrorToasterOpened(): boolean {
        return this._isComboErrorToasterOpened;
    }

    setComboErrorToasterOpened(comboErrorToasterOpened: boolean): void {
        this._isComboErrorToasterOpened = comboErrorToasterOpened;
    }
}
